import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X, SendHorizontal, History, ArrowLeft, FolderOpen, Square, AlertTriangle, RefreshCw } from 'lucide-react';
import { format } from './content.fallback';
import Markdown from './Markdown';
import Attachments from './Attachments';
import HistoryList from './HistoryList';
import LibraryList from './LibraryList';
import ProjectPicker from './ProjectPicker';
import ModelPicker from './ModelPicker';
import ConnectorsPicker from './ConnectorsPicker';
import ThoughtProcess, { extractThinkingContent } from './ThoughtProcess';


/** Maps an error_code to a short, bold heading shown above the error detail. */
const ERROR_HEADINGS = {
    service_unavailable: "Assistant unavailable",
    timeout:             "Response timed out",
    auth_failed:         "Authentication error",
    rate_limited:        "Too many requests",
    context_too_long:    "Conversation too long",
    unknown:             "Something went wrong",
};

/**
 * Renders an error bubble like ChatGPT / Claude:
 *   ⚠  [heading]
 *   [detail message]
 *   [Retry ↺]
 */
const ErrorMessage = ({ message, onRetry, isSending }) => {
    const heading = ERROR_HEADINGS[message.errorCode] || ERROR_HEADINGS.unknown;
    return (
        <div className="sai-error-bubble">
            <div className="sai-error-bubble__header">
                <AlertTriangle size={14} aria-hidden="true" className="sai-error-bubble__icon" />
                <span className="sai-error-bubble__heading">{heading}</span>
            </div>
            <p className="sai-error-bubble__detail">{message.content}</p>
            {onRetry && (
                <button
                    type="button"
                    className="sai-error-bubble__retry"
                    onClick={onRetry}
                    disabled={isSending}
                    aria-label="Retry"
                >
                    <RefreshCw size={12} aria-hidden="true" />
                    Retry
                </button>
            )}
        </div>
    );
};

/**
 * The slide-in chat panel. Presentational — all state lives in AiAssistant.
 */
const ChatPanel = ({
    isOpen,
    waitingForOutput,
    restoredProject,
    token,
    organizationId,
    onSelectProject,
    copy,
    connectors,
    connector,
    messages,
    inputValue,
    selectedProjectId,
    selectedProjectName,
    selectedProjectPrompts,
    selectedModel,
    onModelChange,
    isSending,
    onClose,
    onClear,
    onInputChange,
    onSubmit,
    onStop,
    onRetry,
    onPromptClick,
    onDownload,
    onSelectConnector,
    selectedConnectors = [],
    onToggleConnector,
    showHistory,
    onToggleHistory,
    conversations,
    conversationsLoading,
    conversationsError,
    conversationId,
    onSelectConversation,
    onDeleteConversation,
    deletingConversationId,
    aiAccessible,
}) => {
    const bodyRef = useRef(null);
    const inputRef = useRef(null);
    const panelRef = useRef(null);
    const navigate = useNavigate();

    const activeConnector = connector;
    const connectorsList = connectors || [];
    const welcomeConnectors = [...connectorsList, {
        id: 'hybrid',
        label: 'Hybrid',
        description: 'Use all MCP connectors together',
        available: connectorsList.some((item) => item.available === true),
    }];
    const handleSelectConnector = onSelectConnector;

    const [showLibrary, setShowLibrary] = useState(false);
    const [isProjectManagerOpen, setIsProjectManagerOpen] = useState(false);

    const hasMessages = messages.length > 0;
    const projectPrompts = Array.isArray(selectedProjectPrompts)
        ? selectedProjectPrompts.filter((prompt) => typeof prompt === 'string' && prompt.trim())
        : [];
    const connectorPrompts = (selectedConnectors || [])
        .flatMap((id) => connectorsList.find((p) => p.id === id)?.prompts || []);
    const suggestedPrompts = projectPrompts.length
        ? projectPrompts
        : (connectorPrompts.length ? connectorPrompts.slice(0, 8) : (activeConnector?.prompts || []));

    // Keep the newest message in view as the conversation grows.
    //
    // Scrolls the transcript container directly instead of calling
    // scrollIntoView on a trailing marker: scrollIntoView walks up and scrolls
    // *every* scrollable ancestor, which now includes the page itself since the
    // panel is portaled to document.body. Setting scrollTop can only ever move
    // this one element.
    useEffect(() => {
        if (!isOpen || !hasMessages) return;
        const el = bodyRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, [messages, isOpen, hasMessages, waitingForOutput]);

    // Move focus into the panel when it opens so keyboard users land in the
    // input rather than back at the top of the page.
    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => inputRef.current?.focus(), 250);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // Escape closes the panel.
    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (event) => {
            if (event.key === 'Escape' && !event.defaultPrevented) onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, onClose]);

    // Load the suggestion into the composer and put the cursor at the end, so
    // the user can edit or extend it before sending.
    const handlePromptClick = (prompt) => {
        onPromptClick(prompt);

        const input = inputRef.current;
        if (!input) return;

        input.focus();
        // The value arrives via state, so wait for the re-render before moving
        // the caret — otherwise we'd be seeking within the old value.
        requestAnimationFrame(() => {
            const end = input.value.length;
            input.setSelectionRange(end, end);
        });
    };

    const handleKeyDown = (event) => {
        // Enter sends, Shift+Enter inserts a newline.
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            onSubmit();
        }
    };

    const canSubmit = inputValue.trim().length > 0 && !isSending;

    return (
        <div
            ref={panelRef}
            className={`sai-panel${isOpen ? ' sai-panel--open' : ''}`}
            role="dialog"
            aria-label={copy.panel.aria_label}
            aria-modal="false"
            aria-hidden={!isOpen}
            // Keep the whole subtree out of the tab order while closed —
            // visibility:hidden handles most browsers, but not all.
            inert={!isOpen ? '' : undefined}
        >
            <div className="sai-header">
                <span className="sai-header__icon">
                    <Sparkles size={20} aria-hidden="true" />
                </span>
                <h2 className="sai-header__title">
                    {showHistory
                        ? copy.panel.history_title
                        : showLibrary
                        ? (selectedProjectName ? `${selectedProjectName} Library` : 'Project Library')
                        : copy.panel.title}
                </h2>
                {aiAccessible && (
                <button
                    type="button"
                    id="ai-history-btn"
                    className="sai-header__icon-btn"
                    onClick={() => {
                        if (showLibrary) {
                            setShowLibrary(false);
                        } else {
                            onToggleHistory();
                        }
                    }}
                    aria-label={
                        showHistory || showLibrary ? copy.header.back_aria : copy.header.history_aria
                    }
                    aria-pressed={showHistory || showLibrary}
                    title={
                        showHistory || showLibrary
                            ? copy.header.back_tooltip
                            : copy.header.history_tooltip
                    }
                >
                    {showHistory || showLibrary ? (
                        <ArrowLeft size={18} aria-hidden="true" />
                    ) : (
                        <History size={18} aria-hidden="true" />
                    )}
                </button>
                )}
                {activeConnector && !showHistory && !showLibrary && (
                    <div id="ai-connectors-picker">
                        <ConnectorsPicker
                            connectors={connectorsList}
                            selectedConnectors={selectedConnectors}
                            onToggleConnector={onToggleConnector}
                            disabled={isSending}
                        />
                    </div>
                )}
                {isOpen && aiAccessible && (
                    <div id="ai-project-picker">
                        <ProjectPicker
                            key={`${organizationId || 'no-organisation'}-${restoredProject?.version || ''}`}
                            initialProjectId={restoredProject?.id}
                            onSelectProject={onSelectProject}
                            token={token}
                            organizationId={organizationId}
                            disabled={isSending}
                            isManagerOpen={isProjectManagerOpen}
                            onManagerOpenChange={setIsProjectManagerOpen}
                        />
                    </div>
                )}
                {!showHistory && !showLibrary && aiAccessible && (
                    <button
                        type="button"
                        id="ai-clear-btn"
                        className="sai-header__clear"
                        onClick={onClear}
                        disabled={!hasMessages || isSending}
                        aria-label={copy.header.clear_aria}
                    >
                        {copy.header.clear}
                    </button>
                )}
                <button
                    type="button"
                    id="ai-close-btn"
                    className="sai-header__close"
                    onClick={onClose}
                    aria-label={copy.header.close_aria}
                >
                    <X size={20} aria-hidden="true" />
                </button>
            </div>

            <div className="sai-body" ref={bodyRef}>
                {showHistory ? (
                    <HistoryList
                        copy={copy.history}
                        conversations={conversations}
                        isLoading={conversationsLoading}
                        error={conversationsError}
                        activeConversationId={conversationId}
                        onSelect={onSelectConversation}
                        onDelete={onDeleteConversation}
                        deletingId={deletingConversationId}
                    />
                ) : showLibrary ? (
                    <LibraryList
                        token={token}
                        organizationId={organizationId}
                        projectId={selectedProjectId}
                        projectName={selectedProjectName}
                    />
                ) : aiAccessible === false ? (
                    <div className="sai-upgrade">
                        <div className="sai-upgrade__card">
                            <AlertTriangle size={32} className="sai-upgrade__icon" />
                            <h3 className="sai-upgrade__title">AI Assistant Unavailable</h3>
                            <p className="sai-upgrade__text">
                                Your current plan does not include AI credits. Upgrade your plan to start using the AI Assistant.
                            </p>
                            <button
                                type="button"
                                className="sai-upgrade__btn"
                                onClick={() => {
                                    onClose();
                                    navigate('/settings/my-plan');
                                }}
                            >
                                Upgrade Plan
                            </button>
                        </div>
                    </div>
                ) : aiAccessible === null ? (
                    <div className="sai-welcome">
                        <div className="sai-typing" aria-label="Checking plan...">
                            <span className="sai-typing__dot" />
                            <span className="sai-typing__dot" />
                            <span className="sai-typing__dot" />
                        </div>
                    </div>
                ) : !activeConnector ? (
                    /* Nothing is asked until a connector is chosen */
                    <div className="sai-welcome">
                        <p className="sai-welcome__subheading">
                            {copy.connector_picker?.heading}
                        </p>
                        <div id="ai-connectors-picker" className="sai-connectors">
                            {welcomeConnectors.map((item) => {
                                const available = item.available === true;
                                return (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`sai-connector${
                                        available ? '' : ' sai-connector--soon'
                                    }`}
                                    onClick={() =>
                                        available && handleSelectConnector?.(item.id)
                                    }
                                    disabled={!available}
                                    title={
                                        available
                                            ? item.label
                                            : format(
                                                  copy.connector_picker?.unavailable_tooltip,
                                                  { connector: item.label }
                                              )
                                    }
                                >
                                    <span className="sai-connector__icon">
                                        <Sparkles size={18} aria-hidden="true" />
                                    </span>
                                    <span className="sai-connector__body">
                                        <span className="sai-connector__name">
                                            {item.label}
                                        </span>
                                        <span className="sai-connector__desc">
                                            {item.description}
                                        </span>
                                    </span>
                                    {!available && (
                                        <span className="sai-connector__badge">
                                            {copy.connector_picker?.coming_soon_badge}
                                        </span>
                                    )}
                                </button>
                                );
                            })}
                        </div>
                    </div>
                ) : !hasMessages ? (
                    <div className="sai-welcome">
                        <p className="sai-welcome__subheading">
                            {copy.welcome.subheading}
                        </p>
                        <div className="sai-pills">
                            {suggestedPrompts.map((prompt, index) => (
                                <button
                                    key={`${index}-${prompt}`}
                                    type="button"
                                    className="sai-pill"
                                    onClick={() => handlePromptClick(prompt)}
                                    disabled={isSending}
                                >
                                    <span className="sai-pill__icon">
                                        <Sparkles size={16} aria-hidden="true" />
                                    </span>
                                    <span className="sai-pill__text">{prompt}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="sai-messages">
                        {messages.map((message, index) => {
                            const isAssistant = message.role === 'assistant';
                            const isLastMessage = index === messages.length - 1;
                            const isActivelyStreaming = isSending && isAssistant && isLastMessage;

                            const { thinking: thinkingText, answer: answerText } = isAssistant
                                ? extractThinkingContent(message.content)
                                : { thinking: '', answer: message.content };

                            const hasThoughts = Boolean(
                                (Array.isArray(message.steps) && message.steps.length > 0) ||
                                thinkingText ||
                                message.currentStatus ||
                                (isActivelyStreaming && !answerText)
                            );

                            const shouldRender = Boolean(
                                answerText ||
                                message.content ||
                                message.role === 'error' ||
                                (isAssistant && (hasThoughts || isActivelyStreaming))
                            );

                            if (!shouldRender) return null;

                            return (
                                <div
                                    key={message.id}
                                    className={`sai-message sai-message--${message.role === 'credit_error' ? 'error' : message.role === 'aborted' ? 'assistant' : message.role}`}
                                >
                                    {message.role === 'credit_error' ? (
                                        <div className="sai-credit-error">
                                            <AlertTriangle size={18} className="sai-credit-error__icon" />
                                            <p className="sai-credit-error__text">{message.content}</p>
                                        </div>
                                    ) : message.role === 'aborted' ? (
                                        <div className="sai-aborted-message">
                                            <Square size={14} className="sai-aborted-message__icon" />
                                            <span className="sai-aborted-message__text">{message.content}</span>
                                        </div>
                                    ) : message.role === 'assistant' ? (
                                        <>
                                            {hasThoughts && (
                                                <ThoughtProcess
                                                    steps={message.steps}
                                                    currentStatus={message.currentStatus}
                                                    thinkingText={thinkingText}
                                                    isStreaming={isActivelyStreaming}
                                                    hasAnswer={Boolean(answerText)}
                                                />
                                            )}
                                            {answerText ? (
                                                <>
                                                    <Markdown content={answerText} />
                                                    <Attachments
                                                        files={message.files}
                                                        onDownload={onDownload}
                                                        copy={copy.attachments}
                                                    />
                                                </>
                                            ) : isActivelyStreaming ? (
                                                <div
                                                    className="sai-typing"
                                                    aria-label={message.currentStatus || copy.composer.thinking_aria}
                                                >
                                                    <span className="sai-typing__dot" />
                                                    <span className="sai-typing__dot" />
                                                    <span className="sai-typing__dot" />
                                                </div>
                                            ) : (
                                                <p className="sai-message__empty text-xs text-gray-400 italic py-1">
                                                    {message.isTruncated
                                                        ? 'The model reached its output token limit during reasoning before completing its answer.'
                                                        : 'No response was generated. Please try again.'}
                                                </p>
                                            )}
                                            {message.isTruncated && !isActivelyStreaming && (
                                                <div className="sai-truncated-alert" role="status">
                                                    <AlertTriangle size={13} aria-hidden="true" className="sai-truncated-alert__icon" />
                                                    <span className="sai-truncated-alert__text">
                                                        Response was cut off because the model reached its output token limit.
                                                    </span>
                                                    {onRetry && (
                                                        <button
                                                            type="button"
                                                            className="sai-truncated-alert__retry"
                                                            onClick={onRetry}
                                                            disabled={isSending}
                                                        >
                                                            Retry
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    ) : message.role === 'error' ? (
                                        <ErrorMessage
                                            message={message}
                                            onRetry={onRetry}
                                            isSending={isSending}
                                        />
                                    ) : (
                                        message.content
                                    )}
                                </div>
                            );
                        })}
                        {waitingForOutput && (
                            <div role="status" className="sai-message sai-message--assistant">
                                AI is taking longer than expected to generate your output. You’ll be notified as soon as it’s ready.
                            </div>
                        )}
                        {isSending && !waitingForOutput && (!messages.length || messages[messages.length - 1]?.role !== 'assistant') && (
                            <div className="sai-typing" aria-label={copy.composer.thinking_aria}>
                                <span className="sai-typing__dot" />
                                <span className="sai-typing__dot" />
                                <span className="sai-typing__dot" />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {activeConnector && !showHistory && !showLibrary && aiAccessible && (
            <div className="sai-footer">
                <div id="ai-chat-input-container" className="sai-input-container">
                    <textarea
                        ref={inputRef}
                        id="ai-chat-input"
                        className="sai-input"
                        rows={1}
                        placeholder={copy.composer.placeholder}
                        value={inputValue}
                        onChange={(event) => onInputChange(event.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isSending}
                        aria-label={copy.composer.aria_label}
                    />
                    {isSending ? (
                        <button
                            type="button"
                            className="sai-submit sai-submit--stop"
                            onClick={onStop}
                            aria-label="Stop generating"
                            title="Stop generating"
                        >
                            <Square size={13} fill="currentColor" aria-hidden="true" />
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="sai-submit"
                            onClick={onSubmit}
                            disabled={!canSubmit}
                            aria-label={copy.composer.send_aria}
                        >
                            <SendHorizontal size={16} aria-hidden="true" />
                        </button>
                    )}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 max-w-[800px] mx-auto mt-2 px-1">
                    <div className="flex items-center gap-2 min-w-0 max-w-[60%]">
                        {selectedProjectName ? (
                            <button
                                type="button"
                                id="ai-project-footer-btn"
                                className="text-xs text-primary truncate hover:underline cursor-pointer focus:outline-none flex items-center gap-1 group text-left p-0 bg-transparent border-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => setIsProjectManagerOpen(true)}
                                title={`Manage project: ${selectedProjectName}`}
                                disabled={isSending}
                                aria-label={`Manage project ${selectedProjectName}`}
                            >
                                <span className="text-gray-500 font-normal">Project:</span>
                                <span className="font-semibold underline decoration-primary/40 underline-offset-2 group-hover:decoration-primary">
                                    {selectedProjectName}
                                </span>
                            </button>
                        ) : (
                            <button
                                type="button"
                                id="ai-project-footer-btn"
                                className="text-xs text-primary truncate hover:underline cursor-pointer focus:outline-none flex items-center gap-1 group text-left p-0 bg-transparent border-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => setIsProjectManagerOpen(true)}
                                title="Manage projects"
                                disabled={isSending}
                                aria-label="Manage projects"
                            >
                                <span className="text-gray-500 font-normal">Project:</span>
                                <span className="font-semibold underline decoration-primary/40 underline-offset-2 group-hover:decoration-primary">
                                    Select Project
                                </span>
                            </button>
                        )}
                        <button
                            type="button"
                            id="ai-library-btn"
                            className="text-xs text-primary hover:underline font-medium flex items-center gap-1 shrink-0 px-2 py-0.5 rounded border border-primary/30 hover:bg-primary/5 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => setShowLibrary(true)}
                            title="View project library files"
                            disabled={isSending}
                        >
                            <FolderOpen size={13} aria-hidden="true" />
                            Library
                        </button>
                    </div>
                <div id="ai-model-picker">
                    <ModelPicker
                        selectedModel={selectedModel}
                        onModelChange={onModelChange}
                        disabled={isSending}
                    />
                </div>
                </div>
                <p className="sai-disclaimer">{copy.composer.disclaimer}</p>
            </div>
            )}
        </div>
    );
};

export default ChatPanel;
